"use client";;
import { default as ButtonNF } from "@/app/ui/forms/ButtonNF";
import AnimatedIcon from "@/app/ui/utils/AnimatedIcon";
import FlexBreak from "@/app/ui/utils/FlexBreak";
import { GoogleAnalyticsEvent } from "@/lib/utils";
import { Metadata } from "next";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ReactNode, Suspense } from "react";
import { BsDiscord } from "react-icons/bs";
import { FcNightLandscape } from "react-icons/fc";
import { MdSmsFailed } from "react-icons/md";
import { TbLogin, TbPlugConnectedX, TbReload, TbTrash } from "react-icons/tb";
import LoginBtn from "./LoginBtn";

enum LoginError {
    AccessDenied = "AccessDenied",
    DiscordVerification = "DiscordVerification",
    not2FA = "not2FA",
    dbError = "dbError",
    OAuthCallbackError = "OAuthCallbackError",
    NotOnDiscord = "NotOnDiscord",
    Configuration = "Configuration",
}

const errorMap = {
    [LoginError.DiscordVerification]: {
        title: "משתמש לא מאומת",
        body: (
            <>
                משתמש הדיסקורד שלך לא מאומת, מטעמי אבטחה לא ניתן להתחבר לאתר מבלי לבצע אימות
                <Link className="block text-2xl mt-3" target="_blank"
                    href={ "https://support.discord.com/hc/en-us/articles/6181726888215-Verification-Required-FAQ#docs-internal-guid-9cd047ed-7fff-8e25-e1e6-0cf5644e2255" }
                >איך לאמת משתמש דיסקורד</Link>
                <span className="text-xs">(הליך האימות אינו קשור לנייטפול ומבוצע דרך דיסקורד בלבד)</span>
            </>
        )
    },
    [LoginError.AccessDenied]: {
        title: "אין גישה",
        body: (
            <>
                לא היה ניתן לחבר אותך בעזרת דיסקורד, מקור הבעיה קשור
                <span className="inline-block">
                    לדיסקורד / המשתמש שלך בדיסקורד,
                </span><br />
                <TbPlugConnectedX style={ { display: "inline-block" } } /> אנו מצטערים אבל אין ביכולתנו לעזור.
            </>
        )
    },
    [LoginError.not2FA]: {
        title: "נדרש אימות דו שלבי!",
        body: (
            <>
                <span className="text-2xl block">
                    הינך <b>חבר נייטפול</b> <FcNightLandscape style={ { display: "inline-block" } } size={ "2em" } /> (יאי)
                </span>

                <br />
                אבל מתוך צורכי בטיחות ואבטחה הכניסה לאתר כרגע חסומה עד שתאפשר אימות דו שלבי בחשבון דיסקורד שלך!
                <br />
                אל דאגה זה ממש קל <Link className="block text-2xl mt-3" target="_blank"
                    href={ "https://support.discord.com/hc/en-us/articles/219576828-Setting-up-Multi-Factor-Authentication#h_01J5BFGTKB8GY9HX6M53KBMTJW" }
                >איך להפעיל אימות דו שלבי</Link>
            </>
        )
    },
    [LoginError.dbError]: {
        title: "אופסי דופסי",
        body: (
            <>
                טוב אממ אז זה כבר טעות שלי.. <br />
                יש בעיה בהליך טעינה / יצירת משתמש - יש לדווח לאולימפיק (לי לול)
                <code className="my-4 rounded-md bg-black/20 p-3 block">
                    { new Date().toString() }
                </code>
            </>
        )
    },
    [LoginError.OAuthCallbackError]: {
        title: "בעיות אמון",
        body: (
            <>
                אממ נראה שלא אישרת לאתר לגשת למשתמש דיסקורד שלך..
                <br />
                כאילו זה לגמרי מובן! לכולנו יש לפעמים בעיות אמון קלות וזה לגיטימי לגמרי להיות בטוחים :)
                <br /><br />
                אבל במקרה הזה - הכל טוב, שימ.י לב לגישות שהאתר מבקש - אלו גישות של קריאת מידע, ז&quot;א שלא נוכל לבצע פעולה בשמכם!
                <br /><br />

                וגם לא לדאוג - זה לא אומר שנוכל &quot;לגנוב מידע&quot; חס וחלילה, שימ.י לב איזה פרטים האפליקציה מבקשת לדעת, מדובר בדברים כמו
                הכינוי, הלוגו, האימייל, באילו שרתים את.ה ומה הרולים שלך בשרתים, כל זה מידע ציבורי ונועד אך ורק בכדי שנוכל לזהות ולהציג לכם את המידע באתר.
                <br />
                <br />
                <h4>מבלי שתאשר.י גישה לא נוכל לחבר אותך לאתר</h4>
                <FlexBreak>
                    <section className="flex flex-col gap-2 min-w-40">
                        <span>במידע ושכנענו אותך:</span>
                        <LoginBtn />
                    </section>
                    <section className="flex flex-col gap-2 min-w-40">
                        <span>במידה ולא:</span>
                        <Link href="/" >
                            <ButtonNF color="gray" className="w-full px-8 py-3 text-3xl">לדף הבית</ButtonNF>
                        </Link>
                    </section>
                </FlexBreak>
            </>
        )
    },
    [LoginError.NotOnDiscord]: {
        title: "לא בשרת של נייטפול",
        body: (
            <div className="flex flex-col gap-4">
                המשתמש איתו ניסית להתחבר לא מחובר לשרת הדיסקורד של נייטפול
                <br />
                אל דאגה! זה שנייה להתחבר :)
                <br />
                <Link href={ process.env.NEXT_PUBLIC_DISCORD_INVITE || "" } target="_blank" className="btn">
                    <ButtonNF className="text-4xl" >הכנס לשרת <BsDiscord className="ms-2" /></ButtonNF>
                </Link>
                <p className="text-sm">(לאחר שנכנסת לשרת יש
                    <span className="text-nfBlue-400 font-black mx-2 cursor-pointer" onClick={ () => {
                        GoogleAnalyticsEvent({ event_name: "login", event_value: undefined });
                        signIn("discord", { "redirectTo": "/" })
                    }
                    }>להתחבר מחדש</span>
                    באתר)</p>
            </div>
        )
    },
    [LoginError.Configuration]: {
        title: "שגיאת אימות",
        body: (
            <div className="flex flex-col gap-4">
                <p>
                    אירעה שגיאה בתהליך האימות. שינויים באתר דורשים ניקוי המטמון של האתר בדפדפן.
                </p>

                <div>
                    <h3 role="heading" className="font-bold">
                        מה אפשר לעשות:
                    </h3>

                    <ol className="list-decimal list-inside space-y-2 text-start flex flex-col items-start">
                        <li className="inline-block">
                            <TbTrash className="me-2 inline text-nfBlue-400" /> נקה את המטמון של הדפדפן.
                        </li>
                        <li className="inline-block">
                            <TbReload className="me-2 inline text-nfBlue-400" /> רענן את הדף באמצעות <kbd className="px-2 mx-1 bg-black/60 rounded inline">Ctrl+Shift+F5</kbd> (או <kbd className="px-2 mx-1 bg-black/60 rounded inline" dir="ltr">⌘+Shift+R</kbd> במק).
                        </li>
                        <li className="inline-block">
                            <TbLogin className="me-2 inline text-nfBlue-400" /> נסה להתחבר שוב.
                        </li>
                    </ol>
                </div>

                <p className="text-sm mt-2">
                    אם הבעיה ממשיכה לאחר ניסיון זה, אפשר לנסות למחוק היסטוריה, או ליצור קשר בדיסקורד.
                </p>
            </div>
        )
    },
}

export default function ErrorLogin({ metadata, children }: { metadata: Metadata, children: ReactNode }) {
    return <Suspense>
        <Suspensed_ErrorLogin metadata={ metadata }>
            { children }
        </Suspensed_ErrorLogin>
    </Suspense>

}

function Suspensed_ErrorLogin({ metadata, children }: { metadata: Metadata, children: ReactNode }) {
    const searchParams = useSearchParams()
    const error = searchParams.get("error");

    //if we DONT have error - exit
    if (!error)
        return children;

    const err = errorMap[error as LoginError];
    metadata.title = err?.title || "שגיאה שרת";
    return <section className="relative z-10 mx-auto w-fit m-auto bg-black/20 shadow-xl rounded-2xl flex justify-center gap-4 flex-wrap px-2 py-4 text-shadow text-shadow-x-0 text-shadow-y-[0.35em] text-shadow-blur-[0.2em]">
        <AnimatedIcon icon="error" className="text-[clamp(250px,30vmin,80vw)]" play noHover />

        <div className="inline-flex gap-4 flex-col items-center flex-1 justify-center p-2 md:pe-16 text-center min-w-96">
            <h1>{ metadata.title } <MdSmsFailed style={ { display: "inline" } } /></h1>
            <div className="text-justify [text-align-last:center] max-w-xl text-last">
                { err?.body || "אממ נראה שנתקלנו בשגיאה... זה מביך, נשמח אם תיצור איתנו קשר כדי שנסדר את הבעיה." }
            </div>
        </div>
    </section>
}